import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Shield, FileText, TrendingUp, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const safetySchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Safety & Compliance",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Safety & Compliance services to keep your fleet safe and compliant with regulations on every mile."
};

const SafetyCompliance = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title="Safety & Compliance"
        description="Keep Your Fleet Safe and Compliant on Every Mile"
        canonical="/safety-compliance"
        schemaJson={safetySchema}
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0 bg-navy-950">
            <img 
              src="https://vibe.filesafe.space/1777392978847194987/assets/dfe84238-fc31-4954-87ac-949f4bf37d57.jpg" 
              alt="Safety & Compliance" 
              className="w-full h-full object-cover object-center opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                Safety & Compliance
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
                Safety & <span className="text-primary">Compliance</span>
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 mb-10 font-medium leading-relaxed">
                "Keep Your Fleet Safe and Compliant on Every Mile"
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto">
                    Get a Free Quote <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              
              {/* Feature 1 */}
              <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <FileText className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Stay Ahead of Regulations</h3>
                <p className="text-slate-600 leading-relaxed">
                  Navigating the constantly changing regulations in the transportation industry can be challenging. Our Safety & Compliance services ensure your business remains up-to-date with the latest rules, minimizing the risk of fines and keeping your operations running smoothly.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <ShieldCheck className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Protect Your Drivers and Fleet</h3>
                <p className="text-slate-600 leading-relaxed">
                  Safety is paramount in the transportation industry. Our services provide regular safety audits, training, and risk assessments to keep your drivers and vehicles secure. By prioritizing safety, we help you reduce accidents and protect both your team and your assets.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <Shield className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Reduce Liability and Financial Risk</h3>
                <p className="text-slate-600 leading-relaxed">
                  Non-compliance can lead to costly penalties and increased liability. We take care of compliance management, so you can focus on your business without worrying about potential risks. Our proactive approach saves you money by preventing issues before they arise.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Boost Your Reputation and Customer Trust</h3>
                <p className="text-slate-600 leading-relaxed">
                  A strong record of safety and compliance builds credibility and trust with your clients. By partnering with us, you demonstrate your commitment to high standards, helping you enhance your reputation and attract more business in a competitive market.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Banner Section */}
        <section className="py-20 bg-navy-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-primary/10"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 max-w-3xl mx-auto leading-tight">
              "Proactive Safety and Compliance Solutions for Your Business"
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/free-consultation" aria-label="Learn more about Safety & Compliance">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
              <Link to="/free-consultation">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </main>

      {/* Stats & Hand Image Section */}
      <section className="py-24 bg-white overflow-hidden border-t border-slate-100">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-[1920px] mx-auto">
          <div className="lg:w-1/2 relative group w-full flex justify-start overflow-hidden lg:overflow-visible">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <img 
              src="https://static.wixstatic.com/media/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png/v1/fill/w_960,h_706,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png" 
              alt="AITIM Capital Card" 
              className="w-full max-w-none lg:w-[110%] lg:-ml-12 drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)] group-hover:-translate-y-4 group-hover:scale-105 transition-all duration-700 relative z-10"
            />
          </div>
          <div className="lg:w-1/2 px-4 lg:pr-12 xl:pr-24 w-full">
            <div className="max-w-xl mx-auto lg:mx-0">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-xs mb-6 tracking-widest uppercase">
                Why Choose Us
              </div>
              <h2 className="text-5xl md:text-7xl font-black text-navy-950 mb-16 tracking-tight">AITIM GROUP</h2>
              
              <div className="grid grid-cols-2 gap-x-8 gap-y-12">
                <div className="group">
                  <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">10+</div>
                  <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                  </div>
                  <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{language === 'ru' ? 'ЛЕТ ОПЫТА' : 'YEARS OF\nEXPERIENCE'}</div>
                </div>
                <div className="group">
                  <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">7000+</div>
                  <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                  </div>
                  <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{language === 'ru' ? 'ГРУЗОВИКОВ В СЕТИ' : 'TRUCKS IN OUR\nNETWORK'}</div>
                </div>
                <div className="group">
                  <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">100%</div>
                  <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                  </div>
                  <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{language === 'ru' ? 'УДОВЛЕТВОРЕННОСТЬ' : 'CUSTOMER\nSATISFACTION'}</div>
                </div>
                <div className="group">
                  <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">20+</div>
                  <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                  </div>
                  <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{language === 'ru' ? 'НАГРАД И ПРИЗНАНИЙ' : 'AWARDS AND\nRECOGNITIONS'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SafetyCompliance;
